import { Modal } from '@studiometa/ui';
import { useResize } from '@studiometa/js-toolkit';
import { toggleClass } from '@studiometa/js-toolkit/utils';

const { props } = useResize();

const FOCUSABLE_ELEMENTS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([tabindex^="-"])`)
  .join(', ');

/**
 * Search component
 */
export default class Search extends Modal {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Search',
  };

  /**
   * Element focused before opening, restored on close.
   * @type {HTMLElement|null}
   */
  previouslyFocused = null;

  /**
   * Close on `ESC` and keep the focus inside the modal.
   * @description Overrides the Modal trap, which also counts the fields of the hidden
   * tabs and therefore lets the focus escape after the last visible element.
   * @param {object} props
   * @param {KeyboardEvent} props.event
   * @param {boolean} props.isUp
   * @param {boolean} props.isDown
   * @param {boolean} props.ESC
   */
  keyed({ event, isUp, isDown, ESC }) {
    if (!this.isOpen) {
      return;
    }

    if (isDown && event.key === 'Tab') {
      this.trapFocus(event);
    }

    if (ESC && isUp) {
      this.close();
    }
  }

  /**
   * trapFocus
   * @param {KeyboardEvent} event
   */
  trapFocus(event) {
    const focusables = [...this.$refs.modal.querySelectorAll(FOCUSABLE_ELEMENTS)].filter(
      (element) => element.getClientRects().length > 0,
    );

    if (!focusables.length) {
      return;
    }

    const first = focusables[0];
    const last = focusables.at(-1);
    const index = focusables.indexOf(document.activeElement);

    if (index < 0) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * restoreFocus
   * @description Gives the focus back to the element which opened the modal,
   * or to the header open button when it is no longer available.
   */
  restoreFocus() {
    const target = this.previouslyFocused;
    this.previouslyFocused = null;

    if (target?.isConnected && target.getClientRects().length > 0) {
      target.focus();
      return;
    }

    this.$refs.open.find((btn) => btn.getClientRects().length > 0)?.focus();
  }

  /**
   * mounted
   */
  mounted() {
    this.moveSearch();
    this.nav = document.querySelector('unistra-navbar');
  }

  /**
   * resized hook
   */
  resized() {
    this.moveSearch();
  }

  /**
   * moveSearch
   * @description on mobile the search is moved outside of the menu.
   */
  moveSearch() {
    const { breakpoint } = props();

    if (['xxs', 'xs', 's'].includes(breakpoint)) {
      const targetElement = document.querySelector('#search-teleport');
      if (!targetElement.contains(this.$el)) {
        targetElement.append(this.$el);
      }
    } else {
      const targetElement = document.querySelector('#search-origin');
      targetElement.append(this.$el);
    }
  }

  /**
   * toggleBtnDisplay
   */
  toggleBtnDisplay() {
    // Toggle class for open buttons
    [
      ...this.$refs.open,
      ...this.$root.$children.Action.filter(
        (actionComponent) =>
          actionComponent.$options.target === 'Search' &&
          actionComponent.$options.effect === 'target.open()',
      ).map((actionComponent) => actionComponent.$el),
    ].forEach((btn) => toggleClass(btn, '!hidden', this.isOpen));

    // Toggle class for close buttons
    [
      ...this.$refs.close,
      ...this.$root.$children.Action.filter(
        (actionComponent) =>
          actionComponent.$options.target === 'Search' &&
          actionComponent.$options.effect === 'target.close()',
      ).map((actionComponent) => actionComponent.$el),
    ].forEach((btn) => toggleClass(btn, '!hidden', !this.isOpen));
  }

  /**
   * onOpen
   */
  onOpen() {
    if (!this.$refs.modal.contains(document.activeElement)) {
      this.previouslyFocused = document.activeElement;
    }
    this.toggleBtnDisplay();
    this.nav.close();

    // keeping it to test if it resolve the bug in production
    const input = this.$el.querySelector('input[name="s"]');
    setTimeout(() => {
      input.focus();
    }, 50);
  }

  /**
   * onClose
   */
  onClose() {
    this.toggleBtnDisplay();
    this.restoreFocus();
  }
}
